import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send } from "lucide-react";
import { api, formatApiError, LOGO_URL } from "@/lib/api";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", {
                email: email.trim().toLowerCase(),
                origin_url: window.location.origin,
            });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid="page-forgot-password">
            <div className="relative hidden overflow-hidden bg-brand-ink text-white lg:block">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-[#141414] to-[#0A0A0A]" />
                <div className="absolute inset-0 grain opacity-50" />
                <div className="relative flex h-full flex-col justify-between p-14">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Cursus Physio" className="h-10 w-auto brightness-0 invert" />
                    </Link>
                    <div>
                        <motion.h1
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="font-display text-6xl leading-[0.95] tracking-tightest"
                        >
                            Mot de passe oublié ?
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="mt-6 max-w-md text-white/70"
                        >
                            Pas de souci — saisissez votre email et nous vous enverrons un lien pour le réinitialiser.
                        </motion.p>
                    </div>
                    <div className="text-xs text-white/40">© {new Date().getFullYear()} Cursus Physio</div>
                </div>
            </div>

            <div className="flex items-center justify-center bg-[#FAFAFA] px-6 py-16">
                <div className="w-full max-w-md">
                    <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-smoke hover:text-brand-ink">
                        <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
                    </Link>
                    {done ? (
                        <div className="mt-10 rounded-2xl border border-brand-line bg-white p-8" data-testid="forgot-password-sent">
                            <Mail className="h-8 w-8 text-brand-orange" />
                            <h2 className="mt-6 font-display text-3xl">Lien envoyé.</h2>
                            <p className="mt-4 text-brand-smoke">
                                Si un compte existe pour <strong>{email}</strong>, vous recevrez un email contenant un lien de réinitialisation valable 1 heure.
                            </p>
                            <p className="mt-3 text-xs text-brand-smoke">
                                Pensez à vérifier votre dossier <em>spam/indésirables</em>.
                            </p>
                        </div>
                    ) : (
                        <>
                            <h2 className="mt-10 font-display text-5xl leading-[0.95] tracking-tightest">Réinitialiser<br />mon mot de passe.</h2>
                            <p className="mt-3 text-brand-smoke">Un email contenant un lien temporaire vous sera envoyé.</p>
                            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-password-form">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider">Email</label>
                                    <div className="relative mt-2">
                                        <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="forgot-password-email"
                                            className="w-full rounded-xl border border-brand-line bg-white py-3.5 pl-11 pr-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                            placeholder="vous@exemple.fr"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting || !email}
                                    data-testid="forgot-password-submit"
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-50"
                                >
                                    {submitting ? "Envoi…" : (<>Envoyer le lien <Send className="h-4 w-4" /></>)}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
